import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  LadderedTradeRequest,
  TradePreviewResponse,
  CancelTradeGroupRequest,
  CancelTradeGroupResponse ,
  MomentumSwingRequest,
  VolatilityLadderTradeRequest,
  BollingerReversionRequest,
  RsiExtremesRequest,
  MacdReversionRequest,
  VwapReversionRequest,
  IchimokuCloudRequest,
  IchimokuEntryConditionsResponse
} from '../types/tradeGroup';

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Generic API fetch wrapper with timeout and error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_CONFIG.tradeGroupBaseUrl}${endpoint}`;

  // Check if we're using a placeholder URL (local development)
  if (API_CONFIG.tradeGroupBaseUrl.includes('your-api-gateway-url') || API_CONFIG.tradeGroupBaseUrl === 'http://localhost:3000') {
    console.error('Trade Groups API not configured. Please set REACT_APP_TRADE_GROUP_API_BASE_URL environment variable.');
    throw new Error('API endpoint not configured. Please deploy the API or set REACT_APP_TRADE_GROUP_API_BASE_URL.');
  }

  // Get authentication token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

/** Create or preview a laddered trade group.
 * When preview=true in request, returns TradePreviewResponse.
 * When preview=false, creates the trade group and returns { tradeGroupId: string }.
 */
export async function createLadderedTradeGroup(
  request: LadderedTradeRequest
): Promise<TradePreviewResponse | { tradeGroupId: string }> {
  console.log('Creating/previewing trade group with request:', request);
  const response = await apiFetch<TradePreviewResponse | { tradeGroupId: string }>('/v1/trade-groups/laddered', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  console.log('Trade group response:', response);
  return response;
}

/**
 * Create a momentum swing trade group
 */
export async function createMomentumSwingTradeGroup(
  request: MomentumSwingRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating momentum swing trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    quote_size: request.quoteSize,
    name: request.name,
    ...(request.origin && { origin: request.origin }),
    ...(request.preview !== undefined && { preview: request.preview }),
    ...(request.momentumStrength && { momentum_strength: request.momentumStrength }),
    ...(request.expectedEntryPrice && { expected_entry_price: request.expectedEntryPrice }),
  };

  const response = await apiFetch<{ tradeGroupId: string }>('/v1/trade-groups/momentum-swing', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create momentum swing trade group response:', response);
  return response;
}

/**
 * Create or preview a volatility ladder trade group.
 * When preview=true in request, returns TradePreviewResponse.
 * When preview=false, creates the trade group and returns { tradeGroupId: string }.
 */
export async function createVolatilityLadderTradeGroup(
  request: VolatilityLadderTradeRequest
): Promise<TradePreviewResponse | { tradeGroupId: string }> {
  console.log('Creating/previewing volatility ladder trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    total_quote_size: request.totalQuoteSize,
    max_price: request.maxPrice, // Backend needs max_price for range calculation
    number_of_rungs: request.numLadders, // Backend uses number_of_rungs, UI uses num_ladders
    ladder_spacing_pct: request.ladderSpacingPct, // ✅ Use request value directly (from ATR auto-config)
    profit_target_pct: request.profitTargetPct, // Use direct field from request
    volatility_period: request.volatilityPeriod,
    initial_deployment_pct: request.initialDeploymentPct,
    low_volatility_threshold: request.lowVolatilityThreshold,
    high_volatility_threshold: request.highVolatilityThreshold,
    extreme_volatility_threshold: request.extremeVolatilityThreshold,
    low_volatility_position_multiplier: request.lowVolatilityPositionMultiplier,
    high_volatility_position_multiplier: request.highVolatilityPositionMultiplier,
    rebalance_sensitivity: request.rebalanceSensitivity,
    min_rebalance_interval_hours: request.minRebalanceIntervalHours,
    // Adaptive mode fields (AVL-US-009)
    adaptive_mode: request.adaptiveMode || false,
    ...(request.adaptiveMode && request.lowerBoundPrice && { lower_bound_price: request.lowerBoundPrice }),
    ...(request.adaptiveMode && request.upperBoundPrice && { upper_bound_price: request.upperBoundPrice }),
    keep_alive: request.keepAlive,
    trailing_stop_enabled: request.trailingStopEnabled,
    trailing_stop_percent: request.trailingStopPercent,
    ...(request.preview !== undefined && { preview: request.preview }),
    ...(request.origin && { origin: request.origin }),
  };

  console.log('Transformed backend request:', backendRequest);

  const response = await apiFetch<TradePreviewResponse | { trade_group_id: string }>('/v1/volatility-ladder-trade', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Volatility ladder trade group response:', response);

  // If it's a preview response, return as-is (already in correct format)
  if ('previews' in response || 'totalTrades' in response) {
    return response as TradePreviewResponse;
  }

  // Handle create response - support both camelCase and snake_case field names
  const createResponse = response as { tradeGroupId?: string; trade_group_id?: string };
  const tradeGroupId = createResponse.tradeGroupId || createResponse.trade_group_id;

  if (!tradeGroupId) {
    console.error('No tradeGroupId found in response:', response);
    throw new Error('Invalid response: missing tradeGroupId');
  }

  return { tradeGroupId };
}

/**
 * Create a Bollinger Mean Reversion trade group
 * Requires productId, name, and quoteSize. Other config comes from global config.
 */
export async function createBollingerReversionTradeGroup(
  request: BollingerReversionRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Bollinger Reversion trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true if not specified
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM', // Send period to backend
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/bollinger-reversion', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Bollinger Reversion trade group response:', response);

  // Transform snake_case to camelCase for UI
  return {
    tradeGroupId: response.trade_group_id
  };
}

export async function createRsiExtremesTradeGroup(
  request: RsiExtremesRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating RSI Extremes trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true if not specified
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM', // Send period to backend
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/rsi-extremes', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create RSI Extremes trade group response:', response);

  // Transform snake_case to camelCase for UI
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create a MACD Mean Reversion trade group
 * Requires productId, name, and quoteSize. Other config comes from global config.
 */
export async function createMacdReversionTradeGroup(
  request: MacdReversionRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating MACD Reversion trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true if not specified
    strategy_metric_period: request.strategyMetricPeriod || 'MEDIUM', // Send period to backend
  };

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/macd-reversion', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create MACD Reversion trade group response:', response);

  // Transform snake_case to camelCase for UI
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create a VWAP Mean Reversion trade group (TASK-036, REQ-024).
 * REQ-014: vwap_period and band_sigma persist on the trade group (per-TG config).
 * REQ-015 TGS-readiness: quote_size flows verbatim into original_quote_size.
 */
export async function createVwapReversionTradeGroup(
  request: VwapReversionRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating VWAP Reversion trade group with request:', request);

  // Transform camelCase to snake_case for backend
  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    strategy_metric_period: request.strategyMetricPeriod,
    vwap_period: request.vwapPeriod,
    band_sigma: request.bandSigma,
  };
  // Group-tier ATR-stop overrides: omit when undefined so the backend persists NULL =
  // "inherit" via the three-tier resolver (matches the Ichimoku omit-when-undefined pattern).
  if (request.atrStopEnabled !== undefined) backendRequest.atr_stop_enabled = request.atrStopEnabled;
  if (request.atrStopMultiplier !== undefined) backendRequest.atr_stop_multiplier = request.atrStopMultiplier;
  if (request.atrMaxStopPct !== undefined) backendRequest.atr_max_stop_pct = request.atrMaxStopPct;

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/vwap-reversion', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create VWAP Reversion trade group response:', response);

  // Transform snake_case to camelCase for UI (AD-005: backend returns flat JSON, no envelope)
  return {
    tradeGroupId: response.trade_group_id
  };
}

/**
 * Create an Ichimoku Cloud trade group (REQ-022, REQ-026).
 * Sibling: createVwapReversionTradeGroup. Manually maps camelCase → snake_case
 * before POST per wire-contracts.md Boundary 3. NO envelope unwrap (AD-008).
 */
export async function createIchimokuCloudTradeGroup(
  request: IchimokuCloudRequest
): Promise<{ tradeGroupId: string }> {
  console.log('Creating Ichimoku Cloud trade group with request:', request);

  // Build snake_case body, omitting optional fields when not provided so the
  // backend falls back to its config-provider defaults.
  const backendRequest: Record<string, unknown> = {
    product_id: request.productId,
    name: request.name,
    quote_size: request.quoteSize,
    paper_trade_mode: request.paperTradeMode ?? true, // Default to true (gradual-rollout posture)
    strategy_metric_period: 'MEDIUM', // Ichimoku is MEDIUM-only (1h) at launch — REQ-022
    stop_style: request.stopStyle,
  };
  if (request.tenkanPeriod !== undefined) backendRequest.tenkan_period = request.tenkanPeriod;
  if (request.kijunPeriod !== undefined) backendRequest.kijun_period = request.kijunPeriod;
  if (request.senkouBPeriod !== undefined) backendRequest.senkou_b_period = request.senkouBPeriod;
  if (request.chikouDisplacement !== undefined) backendRequest.chikou_displacement = request.chikouDisplacement;
  if (request.tkCrossLookback !== undefined) backendRequest.tk_cross_lookback = request.tkCrossLookback;
  if (request.positionSizePct !== undefined) backendRequest.position_size_pct = request.positionSizePct;
  if (request.maxHoldingHours !== undefined) backendRequest.max_holding_hours = request.maxHoldingHours;
  // ICS-006 ATR risk framing — REQ-001 / wire-contracts.md Boundary 3.
  // exit_mode is always sent (defaults to ICHIMOKU_SIGNALS). ATR multipliers omitted
  // when undefined so the backend treats them as semantically NULL.
  if (request.exitMode !== undefined) backendRequest.exit_mode = request.exitMode;
  if (request.atrStopMultiplier !== undefined) backendRequest.atr_stop_multiplier = request.atrStopMultiplier;
  if (request.atrTargetMultiplier !== undefined) backendRequest.atr_target_multiplier = request.atrTargetMultiplier;
  if (request.atrProfitLockMultiplier !== undefined) backendRequest.atr_profit_lock_multiplier = request.atrProfitLockMultiplier;
  if (request.atrProfitLockActivationMultiplier !== undefined) backendRequest.atr_profit_lock_activation_multiplier = request.atrProfitLockActivationMultiplier;
  if (request.atrPeriod !== undefined) backendRequest.atr_period = request.atrPeriod;

  const response = await apiFetch<{ trade_group_id: string }>('/v1/trade-groups/ichimoku-cloud', {
    method: 'POST',
    body: JSON.stringify(backendRequest),
  });
  console.log('Create Ichimoku Cloud trade group response:', response);

  return {
    tradeGroupId: response.trade_group_id,
  };
}

/**
 * Fetch Ichimoku entry/exit conditions for a trade group (REQ-028, REQ-029).
 * Returns flat snake_case JSON, NO envelope. Sibling: getVwapEntryConditions.
 */
export async function getIchimokuEntryConditions(
  tradeGroupId: string
): Promise<IchimokuEntryConditionsResponse> {
  return apiFetch<IchimokuEntryConditionsResponse>(
    `/v1/trade-groups/${tradeGroupId}/ichimoku-entry-conditions`
  );
}

/**
 * Cancel a trade group (full or partial)
 */
export async function cancelTradeGroup(
  request: CancelTradeGroupRequest
): Promise<CancelTradeGroupResponse> {
  console.log('Canceling trade group with request:', request);
  const response = await apiFetch<CancelTradeGroupResponse>('/v1/trade-groups/cancel', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  console.log('Cancel trade group response:', response);
  return response;
}

/**
 * Result of an operator pause/resume action (REQ-014).
 * Backend returns a single-resource `{data, metadata}` envelope; apiFetch does NOT
 * auto-unwrap, so these functions read and return `response.data`.
 */
export interface OperatorPauseResumeResult {
  tradeGroupId: string;
  state: string; // OPERATOR_PAUSED after pause, WATCHING after resume
}

/**
 * Operator-pause a trade group (REQ-003, REQ-014).
 * Transitions the group to OPERATOR_PAUSED; the optional reason is persisted as the
 * operator's note. Returns the unwrapped `data` from the single-resource envelope.
 */
export async function operatorPauseTradeGroup(
  tradeGroupId: string,
  reason?: string
): Promise<OperatorPauseResumeResult> {
  const body: { tradeGroupId: string; reason?: string } = { tradeGroupId };
  if (reason && reason.trim()) {
    body.reason = reason.trim();
  }
  const response = await apiFetch<{ data: OperatorPauseResumeResult; metadata: unknown }>(
    '/v1/trade-groups/operator-pause',
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
  return response.data;
}

/**
 * Operator-resume an OPERATOR_PAUSED trade group (REQ-004, REQ-014).
 * Synchronous OPERATOR_PAUSED → WATCHING transition. Returns the unwrapped `data`.
 */
export async function operatorResumeTradeGroup(
  tradeGroupId: string
): Promise<OperatorPauseResumeResult> {
  const response = await apiFetch<{ data: OperatorPauseResumeResult; metadata: unknown }>(
    '/v1/trade-groups/operator-resume',
    {
      method: 'POST',
      body: JSON.stringify({ tradeGroupId }),
    }
  );
  return response.data;
}

/**
 * Fetch active trade groups with details
 */
export async function getActiveTradeGroupsDetails(): Promise<ActiveTradeGroupsDetailsResponse> {
  return apiFetch<ActiveTradeGroupsDetailsResponse>('/v1/trade-groups?limit=250');
}

/**
 * Fetch trade group details including all orders
 */
export async function getTradeGroupDetails(tradeGroupId: string): Promise<TradeGroupDetailsResponse> {
  return apiFetch<TradeGroupDetailsResponse>(`/v1/trade-groups/${tradeGroupId}`);
}

/**
 * Bollinger entry conditions response - real-time technical indicator values
 */
export interface BollingerEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  bollinger_upper: number | null;
  bollinger_middle: number | null;
  bollinger_lower: number | null;
  band_width: number | null;
  price_position_pct: number | null;  // 0% = at lower, 50% = at middle, 100% = at upper
  rsi: number | null;
  market_regime: string | null;
  trend_strength: number | null;
  relative_volume: number | null;
  s2_pivot_level: number | null;
  pivot_timeframe: string | null;
  entry_conditions: {
    is_price_at_lower_band: boolean;
    is_rsi_oversold: boolean;
    is_ranging_market: boolean;
    is_volume_normal: boolean;
    is_above_pivot_support: boolean;
    all_conditions_met: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch Bollinger entry conditions for a trade group
 * Returns real-time technical indicator values and entry condition status
 */
export async function getBollingerEntryConditions(tradeGroupId: string): Promise<BollingerEntryConditionsResponse> {
  return apiFetch<BollingerEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/entry-conditions`);
}

/**
 * RSI Extremes entry conditions response - real-time technical indicator values
 */
export interface RsiExtremesEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  rsi: number | null;
  rsi_oversold_threshold: number | null;
  market_regime: string | null;
  volatility_state: string | null;
  trend_strength: number | null;
  volatility_percentile: number | null;
  relative_volume: number | null;
  entry_conditions: {
    is_rsi_oversold: boolean;
    is_volatility_acceptable: boolean;
    is_ranging_market: boolean;
    is_weak_trend: boolean;
    is_volatility_percentile_ok: boolean;
    is_volume_normal: boolean;
    all_conditions_met: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch RSI Extremes entry conditions for a trade group
 * Returns real-time technical indicator values and entry condition status
 */
export async function getRsiExtremesEntryConditions(tradeGroupId: string): Promise<RsiExtremesEntryConditionsResponse> {
  return apiFetch<RsiExtremesEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/rsi-entry-conditions`);
}

/**
 * MACD entry conditions response - real-time technical indicator values
 */
export interface MacdEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  macd_line: number | null;
  signal_line: number | null;
  histogram: number | null;
  previous_histogram: number | null;
  rsi: number | null;
  market_regime: string | null;
  trend_hostility: number | null;
  volatility_percentile: number | null;
  volatility_state: string | null;
  s2_pivot_level: number | null;
  pivot_timeframe: string | null;
  entry_conditions: {
    is_histogram_recovering: boolean;
    is_macd_below_signal: boolean;
    is_rsi_moderately_oversold: boolean;
    is_regime_favorable: boolean;
    is_volatility_acceptable: boolean;
    is_above_pivot_support: boolean;
    all_conditions_met: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch MACD entry conditions for a trade group
 * Returns real-time technical indicator values and entry condition status
 */
export async function getMacdEntryConditions(tradeGroupId: string): Promise<MacdEntryConditionsResponse> {
  return apiFetch<MacdEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/macd-entry-conditions`);
}

/**
 * VWAP entry conditions response — real-time σ-scaled VWAP indicator values.
 * Wire shape per PLAN-022 + PLAN-AMEND-006 (entry_conditions.is_above_vwap added).
 * Flat envelope (AD-005); no {data, metadata} wrapper.
 */
export interface VwapEntryConditionsResponse {
  product_id: string;
  strategy_metric_period: string;
  granularity_mins: number;
  current_price: number | null;
  vwap: number | null;
  vwap_upper_band: number | null;
  vwap_lower_band: number | null;
  vwap_deviation_pct: number | null;
  band_sigma: number | null;
  vwap_period: number | null;
  entry_conditions: {
    is_below_lower_band: boolean;
    is_above_vwap: boolean;
    conditions_met_count: number;
  };
  timestamp: string;
}

/**
 * Fetch VWAP entry conditions for a trade group (TASK-037, REQ-020).
 * The backend handler applies σ-band scaling at read time using the trade group's
 * band_sigma — the values returned here match what VwapReversionService uses for
 * actual entry decisions.
 */
export async function getVwapEntryConditions(tradeGroupId: string): Promise<VwapEntryConditionsResponse> {
  return apiFetch<VwapEntryConditionsResponse>(`/v1/trade-groups/${tradeGroupId}/vwap-entry-conditions`);
}

/**
 * Response type for active trade groups count
 */
export interface ActiveTradeGroupsResponse {
    activeTradeGroupsCount: number;
    timestamp: number;
}

/**
 * Trade group details
 */
export interface TradeGroup {
    tradeGroupId: string;
    name: string;
    productId: string;
    state: string;
    tradeGroupType: string;
    isActive: boolean;
    currentPriceStatus: string;
    origin: string;
    buyPriceRangePct: number;
    minBuyPrice: number;
    maxBuyPrice: number;
    currentPrice: number;
    sellPriceRangePct: number;
    minSellPrice: number;
    maxSellPrice: number;
    totalCapturedProfit: number;
    avgCapturedReturn: number;
    openSellOrders: number;
    openSellCostBasis: number;
    openSellTargetProfit: number;        // Potential P&L at sell target prices
    openSellUnrealizedProfit: number;    // Actual unrealized P&L at current market price
    combinedPnl: number;                 // Total P&L (Realized + Unrealized)
    openBuyOrders: number;
    openBuyCostBasis: number;
    openBuyUnrealizedProfit: number;
    totalOpenOrders: number;
    totalFees: number;
    keepAliveCount: number;
    createdAt: number | null;
    lastModified: number | null;
    originalQuoteSize?: number;  // Total allocated capital for this trade group
    currentQuoteSize?: number;   // Remaining allocated capital
    // Volatility Ladder specific fields (legacy - kept for backward compatibility)
    volatilityRegime?: string;  // LOW, NORMAL, HIGH, EXTREME
    volatilityPercentile?: number;  // 0-1.0
    currentPositionMultiplier?: number;  // Calculated multiplier based on regime
    capitalDeployedPct?: number;  // 0-1.0
    lastRebalanceTime?: number | null;  // Timestamp
    lastVolatilityCheck?: number | null;  // Timestamp
    volatilityPeriod?: string;  // VERY_SHORT, SHORT, MEDIUM, etc.
    // Bollinger Reversion specific fields
    bollingerCycleCount?: number;
    bollingerWinCount?: number;
    bollingerLossCount?: number;
    bollingerWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    bollingerCumulativePnl?: number;
    bollingerStrategyMetricPeriod?: string;
    bollingerEntryPrice?: number;
    bollingerEntryRsi?: number;
    bollingerEntryQuoteSize?: number;
    bollingerLowerBand?: number;
    bollingerMiddleBand?: number;
    bollingerUpperBand?: number;
    bollingerPrimaryTarget?: number;
    bollingerExtendedTarget?: number;
    bollingerStopLoss?: number;
    bollingerMarketRegime?: string;
    bollingerEntryTime?: number | null;
    bollingerS2Pivot?: number;
    bollingerPartialExitCompleted?: boolean;
    bollingerPartialExitTime?: number | null;
    bollingerPartialExitPrice?: number;
    // RSI Extremes specific fields
    rsiCycleCount?: number;
    rsiWinCount?: number;
    rsiLossCount?: number;
    rsiWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    rsiCumulativePnl?: number;
    rsiStrategyMetricPeriod?: string;  // VERY_SHORT, SHORT, MEDIUM_SHORT, MEDIUM
    rsiEntryPrice?: number;
    rsiEntryRsi?: number;
    rsiEntryQuoteSize?: number;
    rsiMarketRegime?: string;  // RANGING, TRENDING, etc.
    rsiVolatilityState?: string;  // LOW, MEDIUM, HIGH, EXTREME
    rsiVolatilityPercentile?: number;
    rsiTrendStrength?: number;
    rsiOverboughtThreshold?: number;  // e.g., 70
    rsiOversoldThreshold?: number;  // e.g., 30
    rsiProfitTarget?: number;
    rsiStopLoss?: number;
    rsiTrailingStopActivationPrice?: number;
    rsiTrailingStopDistance?: number;
    rsiCurrentRsi?: number;  // Current RSI level when IN_POSITION
    rsiEntryTime?: number | null;
    rsiPartialExitCompleted?: boolean;
    rsiPartialExitTime?: number | null;
    rsiPartialExitPrice?: number;
    rsiStateChangeReason?: string;  // Human-readable explanation of current state (e.g., "Cycle complete: P&L=0.02, Winner=true")
    // MACD Reversion specific fields
    macdCycleCount?: number;
    macdWinCount?: number;
    macdLossCount?: number;
    macdWinRate?: number;  // Calculated: winCount / (winCount + lossCount), 0.0-1.0
    macdCumulativePnl?: number;
    macdStrategyMetricPeriod?: string;  // VERY_SHORT, SHORT, MEDIUM_SHORT, MEDIUM
    macdEntryPrice?: number;
    macdEntryTime?: number | null;
    macdEntryMacdLine?: number;
    macdEntrySignalLine?: number;
    macdEntryHistogram?: number;
    macdStopLoss?: number;
    macdProfitTarget?: number;
    macdStateChangeReason?: string;
    // VWAP Reversion specific fields (TASK-025, vwap-prefixed keys per backend
    // getVwapReversionDetails in TradeGroupApiHandler)
    vwapCycleCount?: number;
    vwapWinCount?: number;
    vwapLossCount?: number;
    vwapWinRate?: number;
    vwapCumulativePnl?: number;
    vwapStrategyMetricPeriod?: string;
    vwapPeriod?: number;          // Rolling VWAP window (REQ-014)
    vwapBandSigma?: number;       // σ-band multiplier (REQ-014)
    vwapCycleId?: string;
    vwapCycleNumber?: number;
    vwapCycleState?: string;
    vwapEntryPrice?: number;
    vwapEntryTime?: number | null;
    vwapEntryQuoteSize?: number;
    vwapEntryFilledSize?: number;
    vwapEntryVwap?: number;
    vwapEntryUpperBand?: number;
    vwapEntryLowerBand?: number;
    vwapEntryBandWidth?: number;
    vwapEntryVwapDeviationPct?: number;
    vwapStopLoss?: number;
    /** Stop-loss LIMIT price actually on the broker (coinbase_trade_order_log.limit_price);
     *  = trigger × STOP_LIMIT_SLIPPAGE at submit time. Pulled byte-identical to broker. */
    vwapStopLossLimit?: number;
    /** Stop basis the cycle actually used: StopBasis enum name ("ATR"/"FLAT_FALLBACK"), or null. */
    vwapStopBasis?: string | null;
    vwapEffectiveAtrMultiplier?: number | null;
    /** Tier the ATR-stop config resolved at: StopResolutionTier enum name ("GROUP"/"PRODUCT"/"GLOBAL"), or null. */
    vwapEffectiveStopTier?: string | null;
    vwapExitTime?: number | null;
    vwapExitPrice?: number;
    vwapExitVwap?: number;
    vwapExitQuoteSize?: number;
    vwapPartialExitCompleted?: boolean;
    vwapPartialExitTime?: number | null;
    vwapPartialExitPrice?: number;
    vwapPartialExitFilledSize?: number;
    vwapPartialExitQuoteSize?: number;
    vwapPartialExitVwap?: number;
    vwapStateChangeReason?: string;
    // Pause/Resume tracking fields (RSI-PAUSE-001)
    pauseCount?: number;  // Total number of times this trade group has been paused
    lastPauseTime?: number | null;  // Timestamp of most recent pause
    lastPauseReason?: string | null;  // Detailed reason for last pause
    lastResumeTime?: number | null;  // Timestamp of most recent resume
    lastResumeReason?: string | null;  // Detailed reason for last resume
    peakCumulativePnl?: number | null;  // Peak cumulative P&L for drawdown calculation
    peakPnlTime?: number | null;  // Timestamp when peak was reached
    // Volatility Ladder complete details (new format)
    volatilityLadder?: VolatilityLadderDetails;
    // Ichimoku Cloud strategy details (nested, populated for ICHIMOKU_CLOUD trade groups
    // via TradeGroupApiHandler.getIchimokuCloudDetails — Boundary 5)
    ichimokuDetails?: IchimokuTradeGroupDetails;
}

/**
 * Ichimoku Cloud trade-group + most-recent-cycle context, as projected by
 * `TradeGroupApiHandler.getIchimokuCloudDetails`. camelCase keys.
 */
export interface IchimokuTradeGroupDetails {
  // Trade-group-level config
  granularityMins?: number;
  stopStyle?: string;
  positionSizePct?: number;
  cycleCount?: number;
  winCount?: number;
  lossCount?: number;
  cumulativePnl?: number;
  // Most-recent-cycle base (populated when at least one cycle exists)
  cycleId?: string;
  cycleNumber?: number;
  cycleState?: string;
  entryTime?: number | null;
  entryPrice?: number | null;
  entryQuoteSize?: number | null;
  entryFilledSize?: number | null;
  stopLossPrice?: number | null;
  exitTime?: number | null;
  exitPrice?: number | null;
  exitReason?: string | null;
  realizedPnl?: number | null;
  isWinner?: boolean | null;
  stateChangeReason?: string | null;
  // Ichimoku entry attribution (REQ-016)
  entryTenkanSen?: number | null;
  entryKijunSen?: number | null;
  entrySenkouSpanA?: number | null;
  entrySenkouSpanB?: number | null;
  entryChikouSpan?: number | null;
  entryCloudTop?: number | null;
  entryCloudBottom?: number | null;
  entrySignalComponents?: string | null;
  stopLossBasis?: number | null;
  // Exit attribution (REQ-019), populated once cycle is past EXIT_ORDER_FILLED
  exitTenkanSen?: number | null;
  exitKijunSen?: number | null;
  exitSenkouSpanA?: number | null;
  exitSenkouSpanB?: number | null;
  exitChikouSpan?: number | null;
  exitCloudTop?: number | null;
  exitCloudBottom?: number | null;
}

/**
 * Volatility Ladder specific attributes
 * Includes configuration and current state
 */
export interface VolatilityLadderDetails {
  // Configuration fields (static)
  targetOrderAmount: number | null;
  numberOfRungs: number | null;
  ladderSpacingPct: number | null;  // e.g., 0.0032 = 0.32%
  ladderSpacingAmount: number | null;  // Dollar amount between rungs
  volatilityPeriod: string | null;  // VERY_SHORT, SHORT, MEDIUM, etc.
  initialDeploymentPct: number | null;  // 0.15 = 15%
  profitTargetPct: number | null;  // 0.02 = 2%
  adaptiveMode: boolean;
  lowerBoundPrice: number | null;
  upperBoundPrice: number | null;
  
  // Threshold configuration
  lowVolatilityThreshold: number | null;
  highVolatilityThreshold: number | null;
  extremeVolatilityThreshold: number | null;
  lowVolatilityPositionMultiplier: number | null;
  highVolatilityPositionMultiplier: number | null;
  rebalanceSensitivity: number | null;
  minRebalanceIntervalHours: number | null;
  
  // Current state (dynamic)
  currentAtr: number | null;
  averageAtr: number | null;
  volatilityPercentile: number | null;  // 0-1.0
  previousVolatilityPercentile: number | null;
  currentVolatilityRegime: string | null;  // LOW, NORMAL, HIGH, EXTREME
  lastVolatilityCheck: number | null;  // Timestamp
  volatilityCheckIntervalHours: number | null;
  lastRebalanceTime: number | null;  // Timestamp
  capitalDeployedPct: number | null;  // 0-1.0
  lastCapitalDeployment: number | null;  // Timestamp
  lastDipBuyingTime: number | null;  // Timestamp
  candleCount: number | null;
}

/**
 * Response type for active trade groups details
 */
export interface ActiveTradeGroupsDetailsResponse {
    tradeGroups: TradeGroup[];
    count: number;
    timestamp: number;
}

/**
 * Trade order details
 */
export interface TradeOrder {
    clientOrderId: string;
    productId: string;
    side: 'BUY' | 'SELL';
    status: string;
    quoteSize: number;
    baseSize: number;
    limitPrice: number;
    price: number;
    postOnly: boolean;
    orderType: string;
    sellTargetPrice: number | null;
    sellTargetStatus: string | null;
    sellOrderId: string | null;
    sellClientOrderId: string | null;
    origin: string;
    keepAlive: boolean;
    expectedEntryPrice: number | null;
    creationTrigger: string | null;
    cancellationTrigger: string | null;
    createdAt: number | null;
    lastModified: number | null;
    orderId: string | null;
    filledSize: number | null;
    averageFilledPrice: number | null;
    fee: number | null;
    totalFees: number | null;
    totalValueAfterFees: number | null;
    rejectMessage: string | null;
    cancelMessage: string | null;
    coinbaseCreatedTime: number | null;
    lastFillTime: number | null;
}

/**
 * Response type for trade group details with orders
 */
export interface TradeGroupDetailsResponse {
    tradeGroup: TradeGroup;
    orders: TradeOrder[];
    timestamp: number;
}

/**
 * Adjustment log entry
 */
export interface AdjustmentLogEntry {
    logId: number;
    tradeGroupId: string;
    adjustmentType: 'RANGE_CREATED' | 'RANGE_AGED_OUT' | 'CAPITAL_REDEPLOYED';
    priceRangeMin: number | null;
    priceRangeMax: number | null;
    capitalAmount: number | null;
    reason: string;
    createdAt: number;
}

/**
 * Pagination metadata
 */
export interface PaginationMetadata {
    page: number;
    perPage: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

/**
 * Response type for trade group adjustments
 */
export interface TradeGroupAdjustmentsResponse {
    adjustments: AdjustmentLogEntry[];
    pagination: PaginationMetadata;
    timestamp: number;
}

/**
 * Fetch active trade groups count
 */
export async function getActiveTradeGroupsCount(): Promise<ActiveTradeGroupsResponse> {
    return apiFetch<ActiveTradeGroupsResponse>(API_CONFIG.endpoints.activeTradeGroups);
}

/**
 * Fetch adjustment log for a trade group
 * 
 * @param tradeGroupId - ID of the trade group
 * @param page - Page number (default 1)
 * @param perPage - Results per page (default 25)
 * @param adjustmentType - Filter by type (comma-separated for multiple)
 */
export async function getTradeGroupAdjustments(
    tradeGroupId: string,
    page: number = 1,
    perPage: number = 25,
    adjustmentType?: string
): Promise<TradeGroupAdjustmentsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('perPage', perPage.toString());
    if (adjustmentType) {
        params.append('adjustmentType', adjustmentType);
    }
    
    return apiFetch<TradeGroupAdjustmentsResponse>(
        `/v1/trade-groups/${tradeGroupId}/adjustments?${params.toString()}`
    );
}

/**
 * Ladder range entry
 */
export interface LadderRangeEntry {
    rangeId: string;
    tradeGroupId: string;
    rangeMinPrice: number;
    rangeMaxPrice: number;
    deployedCapital: number;
    rangeStatus: 'ACTIVE' | 'APPROACHING_AGEOUT' | 'AGED_OUT';
    createdAt: number;
    lastFillTime: number | null;
    ageOutThreshold: number;
    creationReason: string;
    lastModified: number;
}

/**
 * Ladder ranges summary statistics
 */
export interface LadderRangeSummary {
    totalRanges: number;
    activeRanges: number;
    totalDeployedCapital: number;
    averageRangeWidth: number;
}

/**
 * Response type for trade group ladder ranges
 */
export interface TradeGroupRangesResponse {
    ranges: LadderRangeEntry[];
    summary: LadderRangeSummary;
    timestamp: number;
}

/**
 * Fetch ladder ranges for a trade group
 * 
 * @param tradeGroupId - ID of the trade group
 * @param status - Filter by status (comma-separated for multiple)
 */
export async function getTradeGroupRanges(
    tradeGroupId: string,
    status?: string
): Promise<TradeGroupRangesResponse> {
    const params = new URLSearchParams();
    if (status) {
        params.append('status', status);
    }
    
    const url = status 
        ? `/v1/trade-groups/${tradeGroupId}/ranges?${params.toString()}`
        : `/v1/trade-groups/${tradeGroupId}/ranges`;
    
    return apiFetch<TradeGroupRangesResponse>(url);
}

/**
 * Timeline candle data for candlestick chart
 */
export interface TimelineCandle {
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

/**
 * Timeline event types
 */
export type TimelineEventType =
    | 'BUY_FILL'
    | 'SELL_FILL'
    | 'RANGE_CREATED'
    | 'RANGE_AGED_OUT'
    | 'CAPITAL_REDEPLOYED'
    | 'DIP_BUYING'
    | 'REBALANCE'
    | 'HOT_MONEY_REINVESTMENT';

/**
 * Timeline event for marking bot activity on chart
 */
export interface TimelineEvent {
    type: TimelineEventType;
    timestamp: number;
    price: number | null;
    amount: number | null;
    details: {
        orderId?: string;
        side?: string;
        filledSize?: number;
        fee?: number;
        origin?: string;
        logId?: number;
        adjustmentType?: string;
        priceRangeMin?: number;
        priceRangeMax?: number;
        reason?: string;
    };
}

/**
 * Trade pair representing a buy → sell lifecycle
 */
export interface TradePair {
    buyOrderId: string;
    sellOrderId: string | null;  // Null if sell not yet filled
    buyTimestamp: number;         // Epoch millis
    sellTimestamp: number | null; // Null if sell not yet filled
    buyPrice: number;
    sellPrice: number | null;     // Null if sell not yet filled
    profit: number | null;        // Null if sell not yet filled
    profitPercent: number | null; // Null if sell not yet filled
    creationTrigger?: string | null;      // e.g., INITIAL, DIP_BUY, HOT_MONEY, etc.
    cancellationTrigger?: string | null;  // Reason if cancelled
    orderStatus?: string | null;           // FILLED, OPEN, CANCELLED
}

/**
 * Response type for trade group timeline
 */
export interface TradeGroupTimelineResponse {
    candles: TimelineCandle[];
    events: TimelineEvent[];
    tradePairs: TradePair[];
    tradeGroupCreatedAt: number;
    granularity: number; // minutes
}

/**
 * Fetch timeline data for a trade group
 * 
 * @param tradeGroupId - ID of the trade group
 * @param startDate - Start date (epoch millis), defaults to 7 days ago
 * @param endDate - End date (epoch millis), defaults to now
 * @param granularity - Candle granularity in minutes (1, 5, 15, 60, 240, 1440), defaults to 60
 */
export async function getTradeGroupTimeline(
    tradeGroupId: string,
    startDate?: number,
    endDate?: number,
    granularity?: number
): Promise<TradeGroupTimelineResponse> {
    // Default values
    const now = Date.now();
    const defaultStartDate = now - (7 * 24 * 60 * 60 * 1000); // 7 days ago
    const defaultGranularity = 60; // 1 hour candles

    const params = new URLSearchParams();
    params.append('startDate', (startDate ?? defaultStartDate).toString());
    params.append('endDate', (endDate ?? now).toString());
    params.append('granularity', (granularity ?? defaultGranularity).toString());

    const url = `/v1/trade-groups/${tradeGroupId}/timeline?${params.toString()}`;

    return apiFetch<TradeGroupTimelineResponse>(url);
}
